import type { AxiosInstance } from "axios";
import HttpFactory from "~/repository/factory";
import type {
  SchoolEmailSenderDraftInput,
  SchoolEmailSenderSetup,
  SchoolEmailSenderStatus,
} from "~/types/schoolEmailSender";
import type {
  SchoolAnnouncement,
  SchoolAnnouncementInput,
  SchoolCommunicationAudience,
  SchoolCommunicationAudiencePreview,
  SchoolCommunicationEmailSettings,
  SchoolCommunicationEmailSettingsInput,
  SchoolCommunicationPage,
  SchoolEmailBroadcast,
  SchoolEmailBroadcastInput,
  StudentSchoolAnnouncement,
} from "~/types/schoolCommunications";

const base = "/api/organizations/current/communications/";
const studentBase = "/api/profile/me/announcements/";
const senderBase = "/api/organizations/current/email-sender/";

export default class SchoolCommunicationsModule extends HttpFactory {
  constructor(axios: AxiosInstance) {
    super(axios, "school");
  }

  listAnnouncements(page = 1) {
    return this.get<SchoolCommunicationPage<SchoolAnnouncement>>(
      `${base}announcements/`,
      { page, page_size: 25 },
    );
  }

  createAnnouncement(input: SchoolAnnouncementInput) {
    return this.post<SchoolAnnouncement>(`${base}announcements/`, input);
  }

  updateAnnouncement(
    id: number,
    version: number,
    input: SchoolAnnouncementInput,
  ) {
    return this.patch<SchoolAnnouncement>(`${base}announcements/${id}/`, {
      ...input,
      expected_version: version,
    });
  }

  publishAnnouncement(id: number, version: number) {
    return this.post<SchoolAnnouncement>(
      `${base}announcements/${id}/publish/`,
      {
        expected_version: version,
      },
    );
  }

  deactivateAnnouncement(id: number, version: number) {
    return this.post<SchoolAnnouncement>(
      `${base}announcements/${id}/deactivate/`,
      {
        expected_version: version,
      },
    );
  }

  previewAudience(
    audience: SchoolCommunicationAudience,
    channel: "platform" | "email",
  ) {
    return this.post<SchoolCommunicationAudiencePreview>(
      `${base}audience/preview/`,
      {
        audience,
        channel,
      },
    );
  }

  listEmailBroadcasts(page = 1) {
    return this.get<SchoolCommunicationPage<SchoolEmailBroadcast>>(
      `${base}email-broadcasts/`,
      { page, page_size: 25 },
    );
  }

  createEmailBroadcast(input: SchoolEmailBroadcastInput) {
    return this.post<SchoolEmailBroadcast>(`${base}email-broadcasts/`, input);
  }

  updateEmailBroadcast(
    id: number,
    version: number,
    input: SchoolEmailBroadcastInput,
  ) {
    return this.patch<SchoolEmailBroadcast>(`${base}email-broadcasts/${id}/`, {
      ...input,
      expected_version: version,
    });
  }

  confirmEmailBroadcast(
    id: number,
    version: number,
    scheduledAt: string | null,
  ) {
    return this.post<SchoolEmailBroadcast>(
      `${base}email-broadcasts/${id}/confirm/`,
      {
        expected_version: version,
        scheduled_at: scheduledAt,
      },
    );
  }

  cancelEmailBroadcast(id: number, version: number) {
    return this.post<SchoolEmailBroadcast>(
      `${base}email-broadcasts/${id}/cancel/`,
      {
        expected_version: version,
      },
    );
  }

  getEmailSettings() {
    return this.get<SchoolCommunicationEmailSettings>(`${base}email-settings/`);
  }

  updateEmailSettings(input: SchoolCommunicationEmailSettingsInput) {
    return this.patch<SchoolCommunicationEmailSettings>(
      `${base}email-settings/`,
      input,
    );
  }

  testEmailSettings() {
    return this.post<{ delivery_id: number; status: string }>(
      `${base}email-settings/test/`,
      {},
    );
  }

  getEmailSenderStatus() {
    return this.get<SchoolEmailSenderStatus>(`${senderBase}status/`);
  }

  getEmailSenderSetup() {
    return this.get<SchoolEmailSenderSetup>(senderBase);
  }

  saveEmailSenderDraft(input: SchoolEmailSenderDraftInput) {
    return this.patch<SchoolEmailSenderSetup>(`${senderBase}draft/`, input);
  }

  reuseInboxEmailSender(expectedRevision: number) {
    return this.post<SchoolEmailSenderSetup>(`${senderBase}reuse-inbox/`, {
      expected_revision: expectedRevision,
    });
  }

  testEmailSender(expectedRevision: number) {
    return this.post<SchoolEmailSenderSetup>(`${senderBase}test/`, {
      expected_revision: expectedRevision,
    });
  }

  activateEmailSender(expectedRevision: number) {
    return this.post<SchoolEmailSenderSetup>(`${senderBase}activate/`, {
      expected_revision: expectedRevision,
      confirmed_received: true,
    });
  }

  startEmailSenderOAuth(provider: "google" | "microsoft") {
    return this.post<{ authorization_url: string }>(
      `${senderBase}oauth/start/`,
      {
        provider,
      },
    );
  }

  pendingAnnouncements() {
    return this.forWorkspace("teacher").get<StudentSchoolAnnouncement[]>(
      studentBase,
    );
  }

  acknowledgeAnnouncement(revisionId: number) {
    return this.forWorkspace("teacher").post<{ acknowledged: true }>(
      `${studentBase}${revisionId}/acknowledge/`,
      {},
    );
  }
}
